import * as React from 'react';
import { Card, Button, Tag, Tooltip, Row, Col, Space, Typography, Spin, Popconfirm } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import './MlModelsTable.less';
import { observer } from 'mobx-react-lite';
import { MlModelsManagementStore } from '../../stores/MlModelsManagementStore';
import { LoadedMlModel, TargetMlModelService } from '../../types';

interface Props {
    store: MlModelsManagementStore;
}

const { Text } = Typography;

const MlModelsTable: React.FC<Props> = ({ store }) => {
    // Handler for unloading a model (confirmation handled by Popconfirm in UI)
    const handleUnloadModel = async (modelId: string, service: string) => {
        // Map service string to TargetMlModelService enum
        const targetService =
            service === 'SmartTags' ? TargetMlModelService.SmartTags : TargetMlModelService.TextProcessing;
        await store.unloadModel(modelId, targetService);
    };

    // Format date for display with relative time for recent activity
    const formatDate = (date: string | null): string => {
        if (!date) {
            return 'N/A';
        }

        const activityDate = new Date(date);
        const now = new Date();
        const diffInMs = now.getTime() - activityDate.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

        // Less than a minute ago
        if (diffInMinutes < 1) {
            return 'Less than a minute ago';
        }

        // Less than an hour ago - show minutes
        if (diffInMinutes < 60) {
            return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
        }

        // Less than 12 hours ago - show hours
        if (diffInHours < 12) {
            return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
        }

        // More than 12 hours ago - show full date and time
        return activityDate.toLocaleString();
    };

    // Get color for service tag based on service type
    const getServiceColor = (service: string): string => {
        return service === 'SmartTags' ? 'blue' : 'green';
    };

    // Get color for hosting type tag
    const getHostingTypeColor = (hostingType: string): string => {
        const colors: { [key: string]: string } = {
            'Self-hosted': 'orange',
            'Remote provider': 'purple',
            Unknown: 'default'
        };
        return colors[hostingType] || 'default';
    };

    // Show loading spinner when data is being fetched
    if (store.loading && store.loadedModels.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    // Show empty state when no models are loaded
    if (store.loadedModels.length === 0) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    No ML models are currently loaded
                </div>
            </Card>
        );
    }

    return (
        <div
            className="ml-models-table"
            style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', overflowX: 'hidden', padding: '4px' }}
        >
            <Row gutter={[16, 16]}>
                {store.loadedModels.map((model: LoadedMlModel) => (
                    <Col xs={24} sm={24} md={12} lg={8} xl={8} key={model.modelId}>
                        <Card
                            className="ml-model-card"
                            title={
                                <div>
                                    {/* Model name on first line */}
                                    <div style={{ marginBottom: '2px' }}>{model.name}</div>
                                    {/* Model ID on second line for consistent card heights */}
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                                            {model.modelId}
                                        </Text>
                                    </div>
                                </div>
                            }
                            extra={
                                <Tooltip title={model.revision}>
                                    <InfoCircleOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                                </Tooltip>
                            }
                            actions={[
                                <Popconfirm
                                    key="unload"
                                    title={`Unload "${model.name}" from ${model.service}?`}
                                    okText="Unload"
                                    cancelText="Cancel"
                                    okButtonProps={{ danger: true }}
                                    placement="top"
                                    onConfirm={() => handleUnloadModel(model.modelId, model.service)}
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        disabled={store.unloading}
                                        style={{ width: '100%' }}
                                    >
                                        Unload
                                    </Button>
                                </Popconfirm>
                            ]}
                        >
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                {/* Service and Hosting Type tags */}
                                <div>
                                    <Space size="small" wrap>
                                        <Tag color={getServiceColor(model.service)}>{model.service}</Tag>
                                        <Tag color={getHostingTypeColor(model.hostingType)}>{model.hostingType}</Tag>
                                    </Space>
                                </div>

                                {/* Model details */}
                                <div>
                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                        <div>
                                            <Text strong>GPU: </Text>
                                            <Text>{model.gpu}</Text>
                                        </div>
                                        <div>
                                            <Text strong>GPU Memory: </Text>
                                            <Text>{model.gpuMemory}</Text>
                                        </div>
                                        <div>
                                            <Text strong>Last Activity: </Text>
                                            <Text type="secondary">{formatDate(model.lastActivity)}</Text>
                                        </div>
                                    </Space>
                                </div>
                            </Space>
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

export default observer(MlModelsTable);
